import { useId, useRef } from 'react'
import { Paperclip, X } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { cn } from '~/lib/utils'
import { FOCUS_RING } from '../focus'

interface AttachmentFieldProps {
  files: File[]
  onChange: (files: File[]) => void
  maxSizeMb?: number
}

/**
 * Optional extra screenshots or short clips. We do not preview videos to keep
 * the modal compact — the file name is enough confirmation.
 */
export function AttachmentField({
  files,
  onChange,
  maxSizeMb = 25,
}: AttachmentFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const inputId = useId()

  function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? [])
    const accepted: File[] = []
    const tooLarge: string[] = []
    for (const f of picked) {
      if (f.size > maxSizeMb * 1024 * 1024) {
        tooLarge.push(f.name)
      } else {
        accepted.push(f)
      }
    }
    if (tooLarge.length) {
      // Silently drop oversized files but inform the user via console — the
      // host app's toast / banner could pick this up if a UX surface is added.
      console.warn(`[bug-reporter] dropped oversized files: ${tooLarge.join(', ')}`)
    }
    onChange([...files, ...accepted])
    if (inputRef.current) inputRef.current.value = ''
  }

  function remove(idx: number) {
    onChange(files.filter((_, i) => i !== idx))
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        Add more screenshots or recording (optional)
      </p>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="image/*,video/*"
          multiple
          className="sr-only"
          onChange={handlePick}
          data-testid="bug-reporter-attachment-input"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          <Paperclip className="h-3.5 w-3.5" />
          Attach file
        </Button>
        <span className="text-xs text-slate-500">
          Up to {maxSizeMb} MB per file.
        </span>
      </div>

      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700"
            >
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => remove(idx)}
                aria-label={`Remove ${f.name}`}
                className={cn('ml-2 rounded-sm text-slate-500 hover:text-slate-900', FOCUS_RING)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
